import {
  Activity,
  ArrowRight,
  Braces,
  Check,
  ChevronRight,
  CircleAlert,
  Clock3,
  Link2,
  LoaderCircle,
  Paperclip,
  RefreshCcw,
  Send,
  ShieldCheck,
  Sparkles,
  WandSparkles,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  cancelAgentRun,
  createAgentRun,
  executeAgentRun,
  retryAgentRun,
  resumeAgentRun,
  subscribeAgentRunEvents,
} from "../api";
import type {
  AgentRun,
  IncidentItem,
  QodeItem,
  TimelineEvent,
} from "../types";
import { QrPreview } from "../components/QrPreview";
import { absoluteShortUrl, formatRelativeTime } from "../lib/format";

type AgentWorkspaceProps = {
  capabilities: {
    operate: boolean;
    approve: boolean;
  };
  incidents: IncidentItem[];
  qodes: QodeItem[];
  timeline: TimelineEvent[];
  onQodeChanged: (qode: QodeItem, event?: TimelineEvent | null) => void;
  onOpenQodes: () => void;
  onOpenIncidents: () => void;
};

const suggestions = [
  "创建门店活动二维码",
  "检查所有失效风险",
  "整理本周运行摘要",
];

export function AgentWorkspace({
  capabilities,
  incidents,
  qodes,
  timeline,
  onQodeChanged,
  onOpenQodes,
  onOpenIncidents,
}: AgentWorkspaceProps) {
  const [prompt, setPrompt] = useState("");
  const [additionalContext, setAdditionalContext] = useState("");
  const [run, setRun] = useState<AgentRun | null>(null);
  const [working, setWorking] = useState<
    | "planning"
    | "publishing"
    | "requesting"
    | "resuming"
    | "retrying"
    | "cancelling"
    | null
  >(null);
  const [approvalRequested, setApprovalRequested] = useState(false);
  const [actionError, setActionError] = useState("");
  const published = run?.qode?.status === "active";
  const activeIncidents = incidents.filter(
    (incident) => incident.status !== "resolved",
  );

  useEffect(() => {
    if (!run?.id) return;
    return subscribeAgentRunEvents(run.id, {
      onRun: setRun,
    });
  }, [run?.id]);

  const counts = useMemo(
    () => ({
      active: qodes.filter((item) => item.status === "active").length,
      degraded: qodes.filter((item) => item.status === "degraded").length,
      deployments: qodes.reduce((sum, item) => sum + item.deployments, 0),
    }),
    [qodes],
  );

  const createProposal = async () => {
    const goal =
      prompt.trim() || "为本周末的门店活动创建一个可更新、可统计的二维码";
    if (!prompt.trim()) setPrompt(goal);
    setWorking("planning");
    setActionError("");
    try {
      const response = await createAgentRun(goal);
      setRun(response.run);
      setApprovalRequested(false);
      if (response.run.qode) {
        onQodeChanged(response.run.qode);
      }
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Agent 暂时无法生成方案。",
      );
    } finally {
      setWorking(null);
    }
  };

  const publishDraft = async () => {
    if (!run || !run.qode || published) return;
    setWorking(capabilities.approve ? "publishing" : "requesting");
    setActionError("");
    try {
      const response = await executeAgentRun(
        run.id,
        `执行 Agent Run：${run.goal}`,
        run.qode.updatedAt,
      );
      setRun(response.run);
      setApprovalRequested(response.run.status === "waiting_approval");
      if (response.run.qode) {
        onQodeChanged(response.run.qode, response.result?.event);
      }
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Qode 暂时无法发布。",
      );
    } finally {
      setWorking(null);
    }
  };

  const retryRun = async () => {
    if (!run || run.status !== "failed") return;
    setWorking("retrying");
    setActionError("");
    try {
      const response = await retryAgentRun(run.id);
      setRun(response.run);
      if (response.run.qode) {
        onQodeChanged(response.run.qode, response.result?.event);
      }
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Agent 重试失败。",
      );
    } finally {
      setWorking(null);
    }
  };

  const cancelRun = async () => {
    if (!run) return;
    setWorking("cancelling");
    setActionError("");
    try {
      const response = await cancelAgentRun(run.id, "用户在工作台取消运行");
      setRun(response.run);
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Agent 无法取消当前运行。",
      );
    } finally {
      setWorking(null);
    }
  };

  const resumeRun = async () => {
    if (!run || run.status !== "needs_input" || !additionalContext.trim()) return;
    setWorking("resuming");
    setActionError("");
    try {
      const response = await resumeAgentRun(run.id, additionalContext.trim());
      setRun(response.run);
      setAdditionalContext("");
      if (response.run.qode) onQodeChanged(response.run.qode);
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Agent 无法继续当前运行。",
      );
    } finally {
      setWorking(null);
    }
  };

  return (
    <div className="page agent-page">
      <section className="page-heading">
        <div>
          <h1>Qode Agent</h1>
          <p>把目标交给 Agent，它会持续负责数字入口的运行结果。</p>
        </div>
        <div className="agent-state">
          <span />
          Agent 在线
        </div>
      </section>

      <section className="agent-command">
        <div className="agent-command-title">
          <WandSparkles size={19} />
          <strong>今天需要我处理什么？</strong>
        </div>
        <textarea
          value={prompt}
          rows={2}
          disabled={!capabilities.operate}
          placeholder={
            capabilities.operate
              ? "例如：为本周末的门店活动创建一个可更新、可统计的二维码"
              : "当前角色为只读权限"
          }
          onChange={(event) => setPrompt(event.target.value)}
        />
        <div className="command-footer">
          <div className="command-tools">
            <button type="button" aria-label="添加上下文">
              <Paperclip size={16} />
            </button>
            <span>Agent 将先形成方案，公共变更需要确认</span>
          </div>
          <button
            className="primary-button"
            type="button"
            disabled={!capabilities.operate || working !== null}
            onClick={createProposal}
          >
            {working === "planning" ? "正在生成" : "生成方案"}
            {working === "planning" ? (
              <LoaderCircle className="spin" size={15} />
            ) : (
              <Send size={15} />
            )}
          </button>
        </div>
      </section>

      {actionError ? <div className="action-error">{actionError}</div> : null}

      {!run ? (
        <div className="suggestion-row">
          {suggestions.map((suggestion) => (
            <button
              type="button"
              key={suggestion}
              disabled={!capabilities.operate}
              onClick={() => setPrompt(suggestion)}
            >
              <Sparkles size={14} />
              {suggestion}
            </button>
          ))}
        </div>
      ) : run.status === "needs_input" ? (
        <section className="agent-proposal" aria-live="polite">
          <div className="proposal-main">
            <div className="proposal-heading">
              <span className="proposal-icon">
                <CircleAlert size={18} />
              </span>
              <div>
                <h2>还需要一点信息</h2>
                <p>Agent 没有猜测关键业务条件，也没有创建草稿。</p>
              </div>
            </div>
            <div className="validation-line">
              <ShieldCheck size={17} />
              <span>{run.missingInformation.join(" · ")}</span>
            </div>
            <div className="agent-followup">
              <textarea
                rows={3}
                value={additionalContext}
                placeholder="补充业务场景、目标页面或投放条件"
                onChange={(event) => setAdditionalContext(event.target.value)}
              />
              <div className="agent-followup-actions">
                <button
                  className="icon-button"
                  type="button"
                  title="取消当前运行"
                  aria-label="取消当前运行"
                  disabled={working !== null}
                  onClick={() => void cancelRun()}
                >
                  {working === "cancelling" ? (
                    <LoaderCircle className="spin" size={15} />
                  ) : (
                    <X size={16} />
                  )}
                </button>
                <button
                  className="primary-button"
                  type="button"
                  disabled={!additionalContext.trim() || working !== null}
                  onClick={() => void resumeRun()}
                >
                  {working === "resuming" ? (
                    <LoaderCircle className="spin" size={15} />
                  ) : (
                    <ArrowRight size={15} />
                  )}
                  继续运行
                </button>
              </div>
            </div>
          </div>
        </section>
      ) : run.qode ? (
        <section className="agent-proposal" aria-live="polite">
          <div className="proposal-main">
            <div className="proposal-heading">
              <span className={published ? "proposal-icon published" : "proposal-icon"}>
                {published ? <Check size={18} /> : <Sparkles size={18} />}
              </span>
              <div>
                <h2>
                  {run.status === "failed"
                    ? "受控工具执行失败"
                    : run.status === "cancelled"
                      ? "运行已取消"
                      : published
                        ? "入口已经发布"
                        : "我准备好了一个发布方案"}
                </h2>
                <p>
                  {run.status === "failed"
                    ? "发布未被标记为成功。Trace 已保留失败证据，可以在同一 Run 中重试。"
                    : run.status === "cancelled"
                      ? "草稿仍保留在当前 Workspace，可在 Qodes 中继续管理。"
                      : published
                    ? "Qode 正在运行，后续健康变化会进入 Agent 工作台。"
                    : "推荐使用动态入口。海报印刷后仍可修改活动页面，并保留扫描与故障记录。"}
                </p>
              </div>
            </div>

            <dl className="proposal-details">
              <div>
                <dt>入口类型</dt>
                <dd>动态 Qode</dd>
              </div>
              <div>
                <dt>短链接</dt>
                <dd>{run.qode.shortUrl}</dd>
              </div>
              <div>
                <dt>目标页面</dt>
                <dd>{run.qode.destination}</dd>
              </div>
              <div>
                <dt>运行策略</dt>
                <dd>15 分钟健康检查 · 故障时请求批准</dd>
              </div>
            </dl>

            <div className="validation-line">
              <ShieldCheck size={17} />
              <span>{run.plan.validationChecks?.join(" · ")}</span>
            </div>

            <div className="proposal-actions">
              {!published && run.status !== "cancelled" ? (
                <button
                  className="secondary-button"
                  type="button"
                  disabled={working !== null}
                  onClick={() => void cancelRun()}
                >
                  <X size={15} />
                  取消运行
                </button>
              ) : null}
              <button
                className="primary-button"
                type="button"
                disabled={
                  !capabilities.operate ||
                  approvalRequested ||
                  published ||
                  run.status === "cancelled" ||
                  working !== null
                }
                onClick={
                  run.status === "failed"
                    ? () => void retryRun()
                    : publishDraft
                }
              >
                {run.status === "failed"
                  ? working === "retrying"
                    ? "正在重试"
                    : "重试执行"
                  : approvalRequested
                  ? "等待审批"
                  : published
                  ? "已发布"
                  : working === "requesting"
                    ? "正在提交"
                  : working === "publishing"
                    ? "正在发布"
                    : capabilities.approve
                      ? "确认并发布"
                      : "提交审批"}
                {working === "publishing" ||
                working === "requesting" ||
                working === "retrying" ? (
                  <LoaderCircle className="spin" size={15} />
                ) : run.status === "failed" ? (
                  <RefreshCcw size={15} />
                ) : approvalRequested ? (
                  <Clock3 size={15} />
                ) : published ? (
                  <Check size={15} />
                ) : (
                  <ArrowRight size={15} />
                )}
              </button>
            </div>
          </div>
          <div className="proposal-preview">
            <QrPreview value={absoluteShortUrl(run.qode.shortUrl)} size={132} />
            <strong>{run.qode.name}</strong>
            <span>可更新 · 可监测 · 可回滚</span>
          </div>
        </section>
      ) : null}

      {run ? (
        <section className="agent-trace" aria-label="Agent Trace">
          <div className="agent-trace-heading">
            <div>
              <Activity size={18} />
              <div>
                <h2>Agent Trace</h2>
                <span>
                  {run.traceId} · {run.promptVersion}
                </span>
              </div>
            </div>
            <div className={`trace-provider trace-${run.modelObservation.status}`}>
              <Braces size={15} />
              <span>{run.modelProvider}</span>
              <strong>{run.modelObservation.status ?? "unknown"}</strong>
            </div>
          </div>
          <div className="agent-trace-grid">
            <div className="trace-column">
              <h3>执行步骤</h3>
              <ol className="trace-step-list">
                {run.steps.map((step) => (
                  <li key={step.id} className={`trace-step trace-${step.status}`}>
                    <span>{step.sequence}</span>
                    <div>
                      <strong>{step.title}</strong>
                      <p>{step.detail}</p>
                    </div>
                    <small>{step.phase}</small>
                  </li>
                ))}
              </ol>
            </div>
            <div className="trace-column">
              <h3>受控工具</h3>
              {run.toolCalls.length ? (
                <div className="trace-tool-list">
                  {run.toolCalls.map((tool) => (
                    <div className="trace-tool" key={tool.id}>
                      <div>
                        <strong>{tool.toolName}</strong>
                        <span>
                          {tool.resourceId ?? "尚未产生资源"} · 第{" "}
                          {tool.attemptCount} 次
                        </span>
                      </div>
                      <small className={`tool-status tool-${tool.status}`}>
                        {tool.status}
                      </small>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="trace-empty">当前 Run 尚未调用写工具</div>
              )}
            </div>
          </div>
        </section>
      ) : null}

      <section className="workspace-grid">
        <div className="attention-panel">
          <div className="section-title">
            <div>
              <h2>需要处理</h2>
              <span>按现实影响排序</span>
            </div>
            <button type="button" onClick={onOpenIncidents}>
              查看全部
            </button>
          </div>

          <div className="attention-list">
            {activeIncidents.slice(0, 2).map((incident) => (
              <article className="attention-row" key={incident.id}>
                <span className="attention-icon attention-amber">
                  <CircleAlert size={18} />
                </span>
                <div className="attention-copy">
                  <strong>{incident.title}</strong>
                  <span>
                    影响{" "}
                    {incident.impact.deploymentQuantity.toLocaleString("zh-CN")}{" "}
                    个现实部署，{incident.summary}
                  </span>
                </div>
                <div className="impact">
                  <small>
                    {incident.severity === "critical"
                      ? "严重"
                      : incident.severity === "high"
                        ? "高影响"
                        : incident.severity === "medium"
                          ? "中影响"
                          : "低影响"}
                  </small>
                  <strong>
                    {incident.impact.deploymentQuantity.toLocaleString("zh-CN")}
                  </strong>
                </div>
                <button
                  className="row-action"
                  type="button"
                  onClick={onOpenIncidents}
                >
                  审查方案
                  <ChevronRight size={16} />
                </button>
              </article>
            ))}

            {!activeIncidents.length ? (
              <div className="attention-clear">
                <Check size={17} />
                当前没有未解决 Incident
              </div>
            ) : null}

            <article className="attention-row">
              <span className="attention-icon attention-blue">
                <Clock3 size={18} />
              </span>
              <div className="attention-copy">
                <strong>展会反馈入口等待发布</strong>
                <span>验证已完成，尚未登记现实部署。</span>
              </div>
              <div className="impact">
                <small>待确认</small>
                <strong>1</strong>
              </div>
              <button className="row-action" type="button">
                继续处理
                <ChevronRight size={16} />
              </button>
            </article>
          </div>
        </div>

        <aside className="timeline-panel">
          <div className="section-title">
            <div>
              <h2>Agent 动态</h2>
              <span>可追溯的执行记录</span>
            </div>
          </div>
          <div className="timeline">
            {timeline.slice(0, 4).map((event) => (
              <div className="timeline-event" key={event.id}>
                <i className={`timeline-dot timeline-${event.tone}`} />
                <div>
                  <strong>{event.title}</strong>
                  <p>{event.description}</p>
                  <time>{formatRelativeTime(event.time)}</time>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </section>

      <section className="fleet-strip">
        <button className="fleet-summary" type="button" onClick={onOpenQodes}>
          <span>运行中的 Qodes</span>
          <strong>{qodes.length}</strong>
          <ChevronRight size={18} />
        </button>
        <div>
          <span className="summary-dot dot-green" />
          <small>正常</small>
          <strong>{counts.active}</strong>
        </div>
        <div>
          <span className="summary-dot dot-amber" />
          <small>异常</small>
          <strong>{counts.degraded}</strong>
        </div>
        <div>
          <Link2 size={16} />
          <small>现实部署</small>
          <strong>{counts.deployments}</strong>
        </div>
        <div>
          <LoaderCircle size={16} />
          <small>自动监测</small>
          <strong>开启</strong>
        </div>
      </section>
    </div>
  );
}
